import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { isPristine } from 'redux-form'

import { ArtworkStatus } from 'api/enums'
import { getArtwork, getArtworkUploadStatus } from 'redux/reducers'
import * as actions from 'redux/actions'
import * as routes from 'routes'

import View from './View'

class ArtworkView extends Component {
  /**
   * Default constructor.
   */
  constructor (props) {
    super(props)

    // initial state
    this.state = {
      isDeleteDialogVisible: false,
      isCancelEditDialogVisible: false
    }

    this.onSave = this.onSave.bind(this)
    this.onGoBack = this.onGoBack.bind(this)

    this.onRequestPublishing = this.onRequestPublishing.bind(this)
    this.onCancelPublishRequest = this.onCancelPublishRequest.bind(this)
    this.onPublish = this.onPublish.bind(this)
    this.onUnpublish = this.onUnpublish.bind(this)

    this.onShowDeleteDialog = this.onShowDeleteDialog.bind(this)
    this.onHideDeleteDialog = this.onHideDeleteDialog.bind(this)
    this.onDelete = this.onDelete.bind(this)

    this.onToggleDetails = this.onToggleDetails.bind(this)
    this.onHideCancelEditDialog = this.onHideCancelEditDialog.bind(this)
  }

  /**
   * Reload the artwork's data every time the component mounts.
   */
  componentWillMount () {
    const { isUploading } = this.props
    if (!isUploading) {
      const { artworkId, loadArtwork } = this.props
      loadArtwork(artworkId)
    }
  }

  /**
   * Calls the API's artwork update endpoint with the new data when the artwork
   * is saved and the form validation passes.
   */
  onSave (data) {
    const { resetReduxForm, artworkId, isUploading, mergeArtwork } = this.props
    mergeArtwork(data.id, data)

    resetReduxForm(`artwork-${artworkId}`)

    if (isUploading) {
      this.onToggleDetails(true)
    }
  }

  /**
   * Back to artwork list.
   */
  onGoBack () {
    const { history, backRoute } = this.props
    const route = backRoute || routes.artworks
    history.push(route())
  }

  /**
   * Calls the API's artwork update endpoint with new status when the artwork
   * publication is requested.
   */
  onRequestPublishing () {
    const { artwork, mergeArtwork } = this.props
    mergeArtwork(artwork.id, { status: 'PENDING' })
  }

  /**
   * Calls the API's artwork update endpoint with new status when the
   * publication request is cancelled.
   */
  onCancelPublishRequest () {
    const { artwork, mergeArtwork } = this.props
    mergeArtwork(artwork.id, { status: 'DRAFT' })
  }

  /**
   * Calls the API's artwork publish endpoint with a POST request when the
   * artwork publication is authorized by the Manager.
   */
  async onPublish () {
    const { artwork, publishArtwork, loadArtworkCount } = this.props
    await publishArtwork(artwork.id)
    await loadArtworkCount(ArtworkStatus.PENDING)
    await loadArtworkCount(ArtworkStatus.PUBLISHED)
  }

  /**
   * Calls the API's artwork publish endpoint with a DELETE request when the
   * artwork publication is revoked by the Artist or the Manager.
   */
  async onUnpublish () {
    const {
      artwork,
      unpublishArtwork,
      loadArtworkCount
    } = this.props

    await unpublishArtwork(artwork.id)
    await loadArtworkCount(ArtworkStatus.PUBLISHED)
  }

  /**
   * Displays the artwork delete dialog.
   */
  onShowDeleteDialog (event) {
    this.setState({ isDeleteDialogVisible: true })
  }

  /**
   * Hides the artwork delete dialog.
   */
  onHideDeleteDialog () {
    this.setState({ isDeleteDialogVisible: false })
  }

  /**
   * Calls the API's artwork delete endpoint when the artwork deletion is
   * requested.
   */
  async onDelete () {
    const { artworkId, deleteArtwork, isUploading } = this.props

    await deleteArtwork(artworkId)

    if (!isUploading) {
      this.onGoBack()
    }
  }

  onToggleDetails (force = false) {
    const { artworkId, editingArtwork, isPristine } = this.props
    const { isCancelEditDialogVisible } = this.state

    if (editingArtwork === artworkId) {
      const { artworkUploadsHideDetails } = this.props

      if (isPristine || isCancelEditDialogVisible || force) {
        artworkUploadsHideDetails()
        this.onHideCancelEditDialog()
      } else {
        this.setState({
          isCancelEditDialogVisible: true
        })
      }
    } else {
      const { artworkUploadsShowDetails } = this.props

      if (!editingArtwork || isCancelEditDialogVisible) {
        artworkUploadsShowDetails(artworkId)
        this.onHideCancelEditDialog()
      } else {
        this.setState({
          isCancelEditDialogVisible: true
        })
      }
    }
  }

  onHideCancelEditDialog () {
    this.setState({ isCancelEditDialogVisible: false })
  }

  render () {
    const {
      artwork,
      artworkId,
      disableDelete,
      disableDraft,
      enablePublish,
      enableUnpublish,
      isUploading,
      editingArtwork
    } = this.props

    if (artwork.status === ArtworkStatus.DRAFT && disableDraft) {
      this.onGoBack()
    }

    const formName = `artwork-${artworkId}`

    return artwork.id ? (
      <View {...this.props} {...this.state}
        disableDelete={disableDelete}
        enablePublish={enablePublish}
        enableUnpublish={enableUnpublish}
        onSave={this.onSave}
        onGoBack={this.onGoBack}
        onRequestPublishing={this.onRequestPublishing}
        onCancelPublishRequest={this.onCancelPublishRequest}
        onPublish={this.onPublish}
        onUnpublish={this.onUnpublish}
        onShowDeleteDialog={this.onShowDeleteDialog}
        onHideDeleteDialog={this.onHideDeleteDialog}
        onToggleDetails={this.onToggleDetails}
        onHideCancelEditDialog={this.onHideCancelEditDialog}
        onDelete={this.onDelete}
        form={formName}
        isUploading={isUploading}
        isEditing={editingArtwork === artworkId}
      />
    ) : null
  }
}

ArtworkView.propTypes = {
  artwork: PropTypes.object.isRequired
}

const mapStateToProps = (state, { artworkId }) => ({
  artwork: getArtwork(state, artworkId),
  editingArtwork: getArtworkUploadStatus(state).edit,
  isPristine: isPristine(`artwork-${artworkId}`)(state)
})

export default withRouter(connect(mapStateToProps, actions)(ArtworkView))



// WEBPACK FOOTER //
// ./src/containers/ArtworkView/index.js